// App.modular.js — LoadDash Services
// All custom code: original IP ✅ | react MIT ✅ | @supabase MIT ✅
// @stripe MIT ✅ | @capacitor MIT ✅ | leaflet BSD-2 ✅

import React, { useState, useEffect } from "react";

import { NAVY, NAVY2, BLUE, BLUE2, GREEN, RED, AMBER, STEEL, MUTED, BORDER, TEXT2, BG, card, lbl, inp, btn, BRAND_NAME } from "./constants";
import {
  supabase, signOut,
  updateRequestStatus, verifyManualPayment,
  cancelRequest, adminOverrideCancellation, updateRefundStatus,
} from "./services/supabaseService";
import { sendSMSNotification, buildBookingPayload } from "./services/notificationService";
import { triggerAllocator } from "./services/waitlistService";
import { loadDumpRuns, loadFuelSettings, saveFuelSettings, fetchEIAFuelPrice } from "./services/fuelService";
import { useAuth } from "./hooks/useAuth";
import { useRequests } from "./hooks/useRequests";
import { useToast } from "./hooks/useToast";
import { Spinner, Toast } from "./components/Spinner";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import HamburgerMenu from "./components/HamburgerMenu";
import { CancelModal, AdminRefundModal, InfoModal, AddOnModal } from "./components/modals";
import { ErrorBoundary } from "./components/ErrorBoundary";
import ScrollToTop from "./components/ScrollToTop";
import PricingPage   from "./pages/PricingPage";
import ReferralPage  from "./pages/ReferralPage";
import ConfirmedPage from "./pages/ConfirmedPage";
import SchedulePage  from "./pages/SchedulePage";
import LegalScreen   from "./pages/LegalScreen";
import FAQPage       from "./pages/FAQPage";
import { trackEvent, EVENTS } from "./analytics/eventTracker";
import { captureAttribution, clearAttribution } from "./analytics/attribution";
import { clearDraft, loadDraft } from "./utils/bookingDraft";
import PhoneCaptureCard from "./components/PhoneCaptureCard";
import { needsPhoneCapture } from "./utils/phoneCapture";
import { getOwnProfile } from "./services/profileService";
import TipScreen from "./pages/TipScreen";
import { getLatestPayment } from "./utils/paymentUtils";

const BookingFlow       = React.lazy(() => import("./pages/BookingFlow"));
const RequestsPage      = React.lazy(() => import("./pages/RequestsPage"));
const AdminDashboard    = React.lazy(() => import("./pages/AdminDashboard"));
const AdminControlPanel = React.lazy(() => import("./pages/AdminControlPanel"));
const ApprovalBookingFlow = React.lazy(() => import("./pages/ApprovalBookingFlow"));
const RevenueDashboard  = React.lazy(() => import("./pages/RevenueDashboard"));
const MapView           = React.lazy(() => import("./pages/MapView"));
const PrivacyPage       = React.lazy(() => import("./pages/PrivacyPage"));
const AuthScreen        = React.lazy(() => import("./pages/AuthScreen"));
const HomePage          = React.lazy(() => import("./pages/HomePage"));
const AnnouncementsPage = React.lazy(() => import("./pages/AnnouncementsPage"));
const CommunityAlertsPage = React.lazy(() => import("./pages/CommunityAlertsPage"));
const OperatorDashboard = React.lazy(() => import("./pages/OperatorDashboard"));
const DriverManagement  = React.lazy(() => import("./pages/DriverManagement"));

const SORA_STYLE = `@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');*{box-sizing:border-box}input,select,textarea,button{font-family:'Sora',system-ui,-apple-system,sans-serif}`;

export default function App() {
  const {
    session, role, isAdmin, authLoading,
    passwordRecovery,
    newPass, setNewPass, newPassConfirm, setNewPassConfirm,
    newPassBusy, setNewPassBusy, newPassDone, setNewPassDone,
  } = useAuth();

  // Pathname-aware tab initialization: /approval/* → approval
  const path = window.location.pathname;
  const isApprovalRoute = path.startsWith("/approval/");
  const isAdminRoute = path.startsWith("/admin");
  const initialTab = isApprovalRoute ? "approval" : isAdminRoute ? "admin" : "home";
  const [tab, setTab]                   = useState(initialTab);
  const [reqFilter, setReqFilter]       = useState("all");
  const { reqs, reqsLoading, loadReqs } = useRequests(session, isAdmin);


  const { toast, showToast }            = useToast();
  const online                         = useOnlineStatus();

  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [stripeReturn,     setStripeReturn]     = useState(null);
  const [dumpRuns,         setDumpRuns]         = useState([]);
  const [fuelSettings,     setFuelSettings]     = useState({ price_per_gallon: 3.44, truck_mpg: 19, fuel_source: "manual", source_provider: "Manual" });
  const [announcement,     setAnnouncement]     = useState(null);
  const [annDismissed,     setAnnDismissed]     = useState(false);
  const [allActiveReqs,    setAllActiveReqs]    = useState([]);
  const [showMap,          setShowMap]          = useState(false);
  const [cancelModal,      setCancelModal]      = useState(null);
  const [refundModal,      setRefundModal]      = useState(null);
  const [infoModal,        setInfoModal]        = useState(null);
  // Tipping: the completed request being tipped (opens TipScreen), and a
  // deep-link id from the completion text's ?tip=<request_id> link that
  // resolves once the user's requests load.
  const [tipRequest,       setTipRequest]       = useState(null);
  const [addOnRequest,     setAddOnRequest]     = useState(null);
  const [pendingTipId,     setPendingTipId]     = useState(null);

  const VALID_TABS = ["home","schedule","confirmed","requests","pricing","referral","pickupschedule","legal","privacydata","deleteaccount","faq","admin","revenue","community_alerts","operator","drivers","control","approval"];

  function nav(t) { setShowMap(false); setTab(VALID_TABS.includes(t) ? t : "home"); }

  function handleResumeCheckout(r) {
    const draft = {
      step: 6,
      addr: r.addr || "",
      unit: r.unit_number || "",
      notes: r.notes || "",
      phone: r.phone || "",
      loadType: r.type || "",
      loadSize: r.size || null,
      pickDate: r.date || "",
      pickWin: r.pickup_window || "",
      zone: r.zone || "",
      isQP: r.quick || false,
      placement: r.placement_location || "Driveway",
      sessionValid: true,
      confirmedLEU: r.confirmed_leu,
      systemAssignedTier: r.classification_result || r.size,
      classificationConfirmed: true,
      pricingUnlocked: true,
      captureSessionId: r.capture_session_id,
      compositeConfidenceScore: r.composite_confidence_score,
    };
    localStorage.setItem("loaddash_booking_draft_v1", JSON.stringify(draft));
    sessionStorage.setItem("ld_resume_booking", "1");
    sessionStorage.setItem("ld_resume_request_id", r.id);
    nav("schedule");
  }

  useEffect(() => {
    if (!session) return;
    if (["requests","home","admin"].includes(tab)) loadReqs();
    if (isAdmin && (tab === "admin" || tab === "revenue")) {
      loadDumpRuns().then(setDumpRuns);
      loadFuelSettings().then(f => { if (f) setFuelSettings(f); });
    }
    if (isAdmin && tab === "home") {
      loadDumpRuns().then(setDumpRuns);
      supabase.from("requests").select("id,size,status,created_at")
        .in("status", ["pending","available","claimed","scheduled","pending_priority","awaiting_priority_fallback","en_route","in_progress"])
        .then(({ data }) => setAllActiveReqs(data || []));
    }
  }, [session, tab]);

  useEffect(() => {
    supabase.from("announcements").select("*").eq("active", true)
      .order("created_at", { ascending: false }).limit(1)
      .then(({ data }) => setAnnouncement(data?.[0] || null));
  }, []);

  useEffect(() => {
    // First-touch UTM capture from the marketing-site handoff. Persists
    // attribution and strips the params from the URL before the Stripe-return
    // handling below reads the (now UTM-free) query string.
    captureAttribution();

    const params = new URLSearchParams(window.location.search);

    // ── Tip deep link + tip checkout return ─────────────────────────────────
    // ?tip=<request_id> comes from the completion text; ?tip_result comes back
    // from the tip's own Stripe Checkout. Handled before (and independent of)
    // the booking-payment return below.
    const tipParam = params.get("tip");
    const tipResult = params.get("tip_result");
    if (tipParam) {
      setPendingTipId(tipParam);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (tipResult) {
      window.history.replaceState({}, "", window.location.pathname);
      if (tipResult === "success") {
        showToast("Tip sent. 100% goes to your Hauler.");
      }
    }

    const paymentResult = params.get("payment");
    const returnReqId   = params.get("request_id");
    let returnSessionId = params.get("session_id");
    // Fallback: if Stripe template variable was not substituted, read session_id from sessionStorage
    if (!returnSessionId || returnSessionId === "{CHECKOUT_SESSION_ID}") {
      try { const pending = JSON.parse(sessionStorage.getItem("ld_stripe_pending") || "{}"); returnSessionId = pending.sessionId || null; } catch { returnSessionId = null; }
    }
    if (paymentResult && returnReqId) {
      window.history.replaceState({}, "", window.location.pathname);
      try { sessionStorage.removeItem("ld_stripe_pending"); } catch {}
      trackEvent(EVENTS.STRIPE_RETURNED, null, { outcome: paymentResult, request_id: returnReqId });
      if (paymentResult === "success") {
        // Stripe booking confirmed — clear first-touch attribution so a later
        // direct visit isn't mis-attributed to this campaign. The attribution
        // was already persisted on the request row at createRequest time.
        clearAttribution();
        setStripeReturn({ success: false, request_id: returnReqId, verifying: true, hasDraft: false });

        // ── Primary path: verify Checkout Session directly with Stripe ───
        // Falls back to DB polling if session_id is missing or verify fails.
        const verifyViaStripe = async () => {
          if (!returnSessionId) return false; // no session_id → fall through to polling
          try {
            const { data: { session: authSession } } = await supabase.auth.getSession();
            if (!authSession?.access_token) return false;
            const { data, error } = await supabase.functions.invoke("verify-checkout-session", {
              body: { checkout_session_id: returnSessionId },
              headers: { Authorization: `Bearer ${authSession.access_token}` },
            });
            if (error || !data?.result) return false;

            if (data.result === "confirmed") {
              clearDraft();
              trackEvent(EVENTS.PAYMENT_COMPLETED, null, { request_id: returnReqId, via: "verify" });
              setStripeReturn({ success: true, request_id: returnReqId, verifying: false, hasDraft: false });
              return true;
            }
            if (data.result === "open") {
              // Session still open — user may not have completed payment
              setStripeReturn({ success: false, request_id: returnReqId, verifying: false, unconfirmed: true, hasDraft: !!loadDraft() });
              return true;
            }
            if (data.result === "expired") {
              setStripeReturn({ success: false, request_id: returnReqId, verifying: false, hasDraft: !!loadDraft() });
              return true;
            }
            if (data.result === "failed") {
              setStripeReturn({ success: false, request_id: returnReqId, verifying: false, hasDraft: !!loadDraft() });
              return true;
            }
            // result === "error" or unknown → fall through to polling
            return false;
          } catch {
            return false; // verify unavailable → fall through to polling
          }
        };

        verifyViaStripe().then(handled => {
          if (handled) return;
          // ── Fallback: poll DB (covers legacy URLs without session_id, or verify failure) ───
          let attempts = 0;
          const poll = setInterval(async () => {
            attempts++;
            try {
              const [{ data: pay }, { data: req }] = await Promise.all([
                supabase.from("payments").select("status").eq("request_id", returnReqId).single(),
                supabase.from("requests").select("status").eq("id", returnReqId).single(),
              ]);
              // Auto-approval promotes clean paid bookings to available /
              // pending_priority; manual-review ones stay pending; scheduled is
              // legacy. Any of these + a succeeded payment means confirmed.
              // (Missing available/pending_priority here made paid bookings show
              // "unconfirmed" on the poll fallback.)
              if (pay?.status === "succeeded" && ["available", "pending_priority", "pending", "scheduled"].includes(req?.status)) {
                clearInterval(poll);
                clearDraft();
                trackEvent(EVENTS.PAYMENT_COMPLETED, null, { request_id: returnReqId, via: "poll" });
                setStripeReturn({ success: true, request_id: returnReqId, verifying: false, hasDraft: false });
              } else if (attempts >= 6) {
                clearInterval(poll);
                setStripeReturn({ success: false, request_id: returnReqId, verifying: false, unconfirmed: true, hasDraft: !!loadDraft() });
              }
            } catch {
              if (attempts >= 6) {
                clearInterval(poll);
                setStripeReturn({ success: false, request_id: returnReqId, verifying: false, unconfirmed: true, hasDraft: !!loadDraft() });
              }
            }
          }, 2000);
        });
      } else {
        trackEvent(EVENTS.PAYMENT_CANCELED, null, { request_id: returnReqId });
        supabase.from("requests").update({ status: "checkout_canceled" }).eq("id", returnReqId).eq("status", "checkout_pending").then(() => {}).catch(() => {});
        supabase.from("payments").update({ status: "canceled" }).eq("request_id", returnReqId).in("status", ["pending_verification"]).then(() => {}).catch(() => {});
        setStripeReturn({ success: false, request_id: returnReqId, verifying: false, hasDraft: !!loadDraft() });
      }
    }
  }, []);

  useEffect(() => {
    if (session) trackEvent(EVENTS.APP_OPEN, session.user.id);
  }, [session]);

  // P2-B (v3 Gate): post-auth phone capture. If a signed-in user has no phone
  // on their profile (Google OAuth doesn't share one), show the Gate screen.
  // Exits are Continue (valid phone saved) or sign-out.
  const [showPhoneCapture, setShowPhoneCapture] = useState(false);
  useEffect(() => {
    if (!session) { setShowPhoneCapture(false); return; }
    let alive = true;
    getOwnProfile(session.user.id).then(profile => {
      if (alive && needsPhoneCapture(profile)) setShowPhoneCapture(true);
    });
    return () => { alive = false; };
  }, [session]);

  // Resolve the ?tip deep link once the user's own requests are loaded.
  // Only a COMPLETED booking that belongs to this account can open the
  // tip screen; anything else clears the pending id silently.
  useEffect(() => {
    if (!pendingTipId || !reqs?.length) return;
    const r = reqs.find(x => x.id === pendingTipId);
    if (r && r.status === "complete") setTipRequest(r);
    else if (r) showToast("Tips open after the pickup is complete.");
    setPendingTipId(null);
  }, [pendingTipId, reqs]);

  async function handleSendTip(amountDollars) {
    if (!tipRequest) return;
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession?.access_token) { showToast("Please sign in again to tip."); return; }
      const { data, error } = await supabase.functions.invoke("create-tip-checkout", {
        body: { request_id: tipRequest.id, amount_cents: Math.round(amountDollars * 100) },
        headers: { Authorization: `Bearer ${authSession.access_token}` },
      });
      if (error || !data?.url) {
        const detail = data?.error === "ALREADY_TIPPED"
          ? "A tip was already sent for this pickup."
          : (data?.message || data?.error || error?.message || "Could not start the tip checkout.");
        showToast(detail);
        if (data?.error === "ALREADY_TIPPED") setTipRequest(null);
        return;
      }
      window.location.href = data.url;
    } catch (e) {
      showToast("Error: " + (e?.message || "tip checkout failed"));
    }
  }

  async function handleDeclareAddOn(description, pieces) {
    if (!addOnRequest) return;
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession?.access_token) { showToast("Please sign in again."); return; }
      const { data, error } = await supabase.functions.invoke("declare-addon", {
        body: { request_id: addOnRequest.id, description, added_mattress_pieces: pieces },
        headers: { Authorization: `Bearer ${authSession.access_token}` },
      });
      if (error || !data?.ok) {
        showToast(data?.message || data?.error || error?.message || "Could not save the add-on.");
        return;
      }
      setAddOnRequest(null);
      showToast("Got it. Your Hauler will see the added items at pickup.");
      loadReqs();
    } catch (e) {
      showToast("Error: " + (e?.message || "add-on failed"));
    }
  }

  async function handleSetStatus(reqId, status) {
    await updateRequestStatus(reqId, status);
    showToast("Updated to " + status);
    const req = reqs.find(r => r.id === reqId);
    if (req?.phone) sendSMSNotification(status, req.phone, buildBookingPayload(req, []), req.id, "neighbor");
    await loadReqs();
    // Trigger waitlist allocator when capacity may have opened
    if (["declined", "canceled_by_admin"].includes(status) && req?.zone && req?.date) {
      triggerAllocator(req.zone, req.date, status);
    }
  }

  async function handleVerifyPayment(reqId) {
    await verifyManualPayment(reqId);
    showToast("Payment verified");
    loadReqs();
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (authLoading) return (
    <div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:BG }}>
      <Spinner />
    </div>
  );

  // ── Password recovery ─────────────────────────────────────────────────────
  if (passwordRecovery) return (
    <div style={{ minHeight:"100vh",background:BG,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 20px",fontFamily:"'Sora',system-ui,sans-serif" }}>
      <style>{SORA_STYLE}</style>
      <div style={{ background:"#fff",borderRadius:20,padding:"32px 28px",width:"100%",maxWidth:400,boxShadow:"0 4px 24px rgba(10,22,40,.10)" }}>
        <div style={{ textAlign:"center",marginBottom:24 }}>
          <div style={{ fontWeight:900,fontSize:22,color:NAVY,letterSpacing:-0.5 }}>TRASH<span style={{ color:BLUE }}>DASH</span></div>
          <div style={{ fontWeight:700,fontSize:18,color:NAVY,marginTop:12 }}>Create New Password</div>
          <div style={{ fontSize:13,color:STEEL,marginTop:6 }}>Choose a strong password for your account</div>
        </div>
        {newPassDone ? (
          <div style={{ textAlign:"center",padding:"20px 0" }}>
            <div style={{ fontWeight:700,fontSize:16,color:NAVY,marginBottom:8 }}>Password Updated!</div>
            <div style={{ fontSize:13,color:STEEL,marginBottom:20 }}>You can now sign in with your new password.</div>
            <button onClick={() => { window.history.replaceState({}, "", "/"); window.location.reload(); }} style={{ width:"100%",padding:14,borderRadius:12,border:"none",background:NAVY,color:"#fff",fontWeight:700,fontSize:15,cursor:"pointer" }}>Continue to App</button>
          </div>
        ) : (
          <>
            <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
              <input type="password" placeholder="New password" value={newPass} onChange={e=>setNewPass(e.target.value)} style={{ width:"100%",background:"#f0f4f8",border:"1px solid #e2e8f0",borderRadius:10,color:NAVY,fontSize:15,padding:"13px 14px",outline:"none",boxSizing:"border-box",fontFamily:"inherit" }}/>
              <input type="password" placeholder="Confirm new password" value={newPassConfirm} onChange={e=>setNewPassConfirm(e.target.value)} style={{ width:"100%",background:"#f0f4f8",border:"1px solid #e2e8f0",borderRadius:10,color:NAVY,fontSize:15,padding:"13px 14px",outline:"none",boxSizing:"border-box",fontFamily:"inherit" }}/>
              {newPass && newPassConfirm && newPass !== newPassConfirm && <div style={{ fontSize:12,color:RED,fontWeight:600 }}>Passwords do not match</div>}
              {newPass && newPass.length < 8 && <div style={{ fontSize:12,color:AMBER,fontWeight:600 }}>Password must be at least 8 characters</div>}
            </div>
            <button onClick={async () => {
              if (newPass !== newPassConfirm || newPass.length < 8) return;
              setNewPassBusy(true);
              const { error } = await supabase.auth.updateUser({ password: newPass });
              setNewPassBusy(false);
              if (error) showToast("Error: " + error.message);
              else setNewPassDone(true);
            }} disabled={newPassBusy || newPass !== newPassConfirm || newPass.length < 8} style={{ width:"100%",padding:14,borderRadius:12,border:"none",background:newPass===newPassConfirm&&newPass.length>=8?NAVY:"#ccc",color:"#fff",fontWeight:700,fontSize:15,cursor:newPass===newPassConfirm&&newPass.length>=8?"pointer":"not-allowed",marginTop:16 }}>
              {newPassBusy ? "Updating..." : "Set New Password"}
            </button>
          </>
        )}
      </div>
    </div>
  );

  // ── Auth gate ─────────────────────────────────────────────────────────────
  if (!session) return (
    <React.Suspense fallback={<div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:"100vh",background:BG }}><Spinner /></div>}>
      <AuthScreen onAuth={() => window.location.reload()} />
    </React.Suspense>
  );

  // ── Main app ──────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily:"'Sora',system-ui,-apple-system,sans-serif",background:BG,minHeight:"100vh",maxWidth:480,margin:"0 auto",position:"relative" }}>
      <style>{SORA_STYLE}</style>
      {showPhoneCapture && <PhoneCaptureCard userId={session.user.id} email={session.user.email} provider={session.user.app_metadata?.provider} onDone={() => setShowPhoneCapture(false)} onSignOut={() => signOut().then(() => window.location.reload())} />}

      <ScrollToTop tab={tab} />
      <HamburgerMenu isAdmin={isAdmin} role={role} onSignOut={() => signOut().then(() => window.location.reload())} onNav={nav} tab={tab} />

      {!online && <div style={{ background:"#fce4e4", borderBottom:"1px solid #ef9a9a", padding:"8px 18px", fontSize:12, fontWeight:600, color:"#c62828", textAlign:"center" }}>No internet connection. Some actions may not work until you're back online.</div>}

      <React.Suspense fallback={<div style={{ display:"flex",alignItems:"center",justifyContent:"center",padding:60 }}><Spinner /></div>}>
        {/* ── Resident pages ── */}
        {tab==="home"          && <HomePage session={session} isAdmin={isAdmin} reqs={reqs} reqsLoading={reqsLoading} allActiveReqs={allActiveReqs} dumpRuns={dumpRuns} announcement={announcement} nav={nav} onShowMap={()=>setShowMap(true)} onVerifyPayment={handleVerifyPayment} onSetStatus={handleSetStatus} onDetail={()=>{}} onCancel={setCancelModal} onManageRefund={setRefundModal} onDecline={r=>handleSetStatus(r.id,"declined")}/>}
        {tab==="schedule"      && <ErrorBoundary label="booking flow"><BookingFlow session={session} reqs={reqs} onComplete={b=>{setConfirmedBooking(b);nav("confirmed");}} showToast={showToast}/></ErrorBoundary>}
        {tab==="confirmed"     && <ConfirmedPage booking={confirmedBooking} onViewRequests={()=>nav("requests")} onHome={()=>nav("home")}/>}
        {tab==="requests"      && <RequestsPage reqs={reqs} reqsLoading={reqsLoading} filter={reqFilter} setFilter={setReqFilter} onDetail={()=>{}} onVerifyPayment={handleVerifyPayment} onSetStatus={handleSetStatus} onCancel={setCancelModal} onResumeCheckout={handleResumeCheckout} onTip={r=>setTipRequest(r)} onAddOn={r=>setAddOnRequest(r)} nav={nav}/>}
        {tab==="pricing"       && <PricingPage nav={nav} session={session}/>}
        {tab==="referral"      && !isAdmin && <ReferralPage/>}
        {tab==="pickupschedule"&& <SchedulePage nav={nav}/>}
        {tab==="legal"         && <LegalScreen/>}
        {tab==="faq"           && <FAQPage/>}
        {tab==="privacydata"   && <PrivacyPage session={session} reqs={reqs}/>}
        {tab==="deleteaccount" && <PrivacyPage session={session} reqs={reqs} startDelete/>}

        {/* ── Admin pages ── */}
        {tab==="admin"         && isAdmin && <ErrorBoundary label="admin dashboard"><AdminDashboard session={session} reqs={reqs} reqsLoading={reqsLoading} dumpRuns={dumpRuns} fuelSettings={fuelSettings} allActiveReqs={allActiveReqs} onVerifyPayment={handleVerifyPayment} onSetStatus={handleSetStatus} onManageRefund={setRefundModal} onDecline={r=>handleSetStatus(r.id,"declined")} onShowMap={()=>setShowMap(true)} showToast={showToast} loadReqs={loadReqs} saveFuelSettings={u=>saveFuelSettings(fuelSettings,u).then(f=>{if(f)setFuelSettings(f);})} fetchEIAFuelPrice={()=>fetchEIAFuelPrice().then(p=>p?showToast(`Fuel updated: $${p}/gal`):showToast("EIA fetch failed"))}/></ErrorBoundary>}
        {tab==="control"       && isAdmin && <AdminControlPanel session={session} reqs={reqs} showToast={showToast} loadReqs={loadReqs} onClose={()=>nav("home")}/>}
        {tab==="revenue"       && isAdmin && <RevenueDashboard reqs={reqs} reqsLoading={reqsLoading} dumpRuns={dumpRuns} fuelSettings={fuelSettings} onVerify={loadReqs}/>}
        {tab==="community_alerts" && isAdmin && <CommunityAlertsPage session={session} showToast={showToast}/>}
        {tab==="operator"      && isAdmin && <OperatorDashboard reqs={reqs} reqsLoading={reqsLoading} showToast={showToast} loadReqs={loadReqs}/>}
        {tab==="drivers"       && isAdmin && <DriverManagement session={session} showToast={showToast}/>}
        {tab==="approval"      && <ApprovalBookingFlow />}

        {/* Fallback — no tab matched */}
        {!VALID_TABS.includes(tab) && (
          <div style={{color:"#fff", background:"#e53935", padding:20, margin:18, borderRadius:12, fontWeight:700}}>Invalid tab: {String(tab)}</div>
        )}
      </React.Suspense>

      {/* ── Map overlay ── */}
      {showMap && (
        <React.Suspense fallback={<Spinner/>}>
          <MapView reqs={reqs} onClose={(dest) => {
            const target = typeof dest === "string" && VALID_TABS.includes(dest) ? dest : tab;
            setShowMap(false);
            setTab(target);
          }}/>
        </React.Suspense>
      )}

      {/* ── Announcement banner — floats over all screens ── */}
      {announcement && !annDismissed && (
        <div style={{ position:"fixed",top:54,left:0,right:0,zIndex:500,maxWidth:480,margin:"0 auto",background:"linear-gradient(135deg,#0a1628 0%,#1565c0 100%)",borderBottom:"2px solid #42a5f5",padding:"14px 16px 12px",boxShadow:"0 4px 24px rgba(10,22,40,.3)" }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12 }}>
            <div style={{ flex:1 }}>
              <div style={{ marginBottom:4 }}>
                <span style={{ fontSize:11,fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:"#42a5f5" }}>Announcement</span>
              </div>
              <div style={{ fontWeight:700,fontSize:14,color:"#fff",marginBottom:3 }}>{announcement.title}</div>
              <div style={{ fontSize:12,color:"rgba(255,255,255,.8)",lineHeight:1.5,whiteSpace:"pre-wrap" }}>{announcement.body}</div>
            </div>
            <button onClick={()=>setAnnDismissed(true)} style={{ background:"rgba(255,255,255,.15)",border:"none",borderRadius:"50%",width:28,height:28,color:"#fff",fontSize:16,cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>
          </div>
        </div>
      )}

      {/* ── Add-on modal (active bookings before the pickup-day cutoff) ── */}
      {addOnRequest && <AddOnModal r={addOnRequest} onClose={() => setAddOnRequest(null)} onSubmit={handleDeclareAddOn} />}

      {/* ── Tip screen overlay (completed bookings only) ── */}
      {tipRequest && (
        <div style={{ position:"fixed", inset:0, zIndex:700, overflowY:"auto", background:"#F2F2F2", maxWidth:480, margin:"0 auto" }}>
          <TipScreen
            loaderName={tipRequest.loader_first_name || "Your Hauler"}
            loaderInitials={(tipRequest.loader_first_name || "H").slice(0,1).toUpperCase()}
            jobTotal={((getLatestPayment(tipRequest.payments)?.amount) || 0) / 100}
            onTipSent={handleSendTip}
            onSkip={() => setTipRequest(null)}
          />
        </div>
      )}

      {/* ── Stripe return modal ── */}
      {stripeReturn && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.6)",zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",padding:24 }}>
          <div style={{ background:"#fff",borderRadius:20,padding:28,width:"100%",maxWidth:400,textAlign:"center",boxShadow:"0 8px 40px rgba(0,0,0,.2)" }}>
            {stripeReturn.verifying ? (
              <>
                <div style={{ fontWeight:800,fontSize:20,color:NAVY,marginBottom:8 }}>Confirming payment...</div>
                <div style={{ fontSize:14,color:STEEL,marginBottom:24,lineHeight:1.5 }}>We are verifying your payment with the processor. This usually takes a few seconds.</div>
                <div style={{ width:32,height:32,border:"3px solid #dce4ef",borderTopColor:BLUE,borderRadius:"50%",animation:"spin 1s linear infinite",margin:"0 auto" }} />
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
              </>
            ) : stripeReturn.unconfirmed ? (
              <>
                <div style={{ fontWeight:800,fontSize:20,color:NAVY,marginBottom:8 }}>Payment processing</div>
                <div style={{ fontSize:14,color:STEEL,marginBottom:16,lineHeight:1.5 }}>Your payment is being processed. This can take up to a minute. You will receive a confirmation once it completes.</div>
                <button onClick={()=>{setStripeReturn(null);loadReqs();nav("requests");}} style={{ width:"100%",padding:14,borderRadius:12,border:"none",background:NAVY,color:"#fff",fontWeight:700,fontSize:15,cursor:"pointer" }}>View My Requests</button>
              </>
            ) : stripeReturn.success ? (
              <>
                <div style={{ fontWeight:800,fontSize:20,color:NAVY,marginBottom:8 }}>Payment Confirmed</div>
                <div style={{ fontSize:14,color:STEEL,marginBottom:24,lineHeight:1.5 }}>Your pickup is scheduled. We will see you on pickup day.</div>
                <button onClick={()=>{setStripeReturn(null);loadReqs();nav("requests");}} style={{ width:"100%",padding:14,borderRadius:12,border:"none",background:NAVY,color:"#fff",fontWeight:700,fontSize:15,cursor:"pointer" }}>View My Requests</button>
              </>
            ) : (
              <>
                <div style={{ fontWeight:800,fontSize:20,color:NAVY,marginBottom:8 }}>Payment Not Completed</div>
                <div style={{ fontSize:14,color:STEEL,marginBottom:16,lineHeight:1.5 }}>No charge was made. Your booking details are saved.</div>
                {stripeReturn.hasDraft && (
                  <button onClick={()=>{setStripeReturn(null);sessionStorage.setItem("ld_resume_booking","1");nav("schedule");}} style={{ width:"100%",padding:14,borderRadius:12,border:"none",background:BLUE,color:"#fff",fontWeight:700,fontSize:15,cursor:"pointer",marginBottom:10 }}>Resume Booking</button>
                )}
                <div style={{ display:"flex",gap:10 }}>
                  <button onClick={()=>{setStripeReturn(null);nav("home");}} style={{ flex:1,padding:14,borderRadius:12,border:`1.5px solid ${BORDER}`,background:"#fff",color:STEEL,fontWeight:700,fontSize:14,cursor:"pointer" }}>Go Home</button>
                  <button onClick={()=>{setStripeReturn(null);nav("requests");}} style={{ flex:1,padding:14,borderRadius:12,border:"none",background:NAVY,color:"#fff",fontWeight:700,fontSize:14,cursor:"pointer" }}>My Requests</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Cancel modal ── */}
      {cancelModal && <CancelModal r={cancelModal} onClose={()=>setCancelModal(null)} onConfirm={async args=>{
        try { await cancelRequest({requestId:cancelModal.id,...args}); if (cancelModal.zone && cancelModal.date) triggerAllocator(cancelModal.zone, cancelModal.date, "cancellation"); setCancelModal(null); loadReqs(); setInfoModal({ title: "Booking cancelled", message: "Your booking has been cancelled. Your slot has been released." }); }
        catch(e) { showToast("Error: "+e.message); }
      }}/>}

      {/* ── Refund modal ── */}
      {refundModal && <AdminRefundModal r={refundModal} onClose={()=>setRefundModal(null)} onSave={async args=>{
        try { await adminOverrideCancellation({requestId:refundModal.id,...args,adminId:session.user.id}); await updateRefundStatus(refundModal.id,args.refundStatus); showToast("Updated"); setRefundModal(null); loadReqs(); }
        catch(e) { showToast("Error: "+e.message); }
      }}/>}

      {infoModal && <InfoModal title={infoModal.title} message={infoModal.message} onClose={() => setInfoModal(null)} actions={infoModal.actions} spinner={infoModal.spinner} />}

      {/* ── ADMIN bottom nav ── */}
      {isAdmin && (
        <div style={{ position:"fixed",bottom:0,left:0,right:0,background:NAVY,display:"flex",minHeight:64,paddingBottom:"env(safe-area-inset-bottom, 0px)",zIndex:100,borderTop:"2px solid #1565c0",maxWidth:480,margin:"0 auto" }}>
          {[
            ["home",         "Home"],
            ["revenue",      "Revenue"],
            ["community_alerts","Alerts"],
          ].map(([t, label]) => (
            <button key={t} onClick={()=>nav(t)} style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,background:"transparent",border:"none",cursor:"pointer",position:"relative",padding:0 }}>
              {tab===t && <div style={{ position:"absolute",top:0,left:"20%",right:"20%",height:2,background:"#42a5f5",borderRadius:"0 0 2px 2px" }}/>}
              <span style={{ fontWeight:700,fontSize:10,letterSpacing:.5,textTransform:"uppercase",color:tab===t?"#42a5f5":"#4a6080" }}>{label}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── RESIDENT bottom nav ── */}
      {!isAdmin && (
        <div style={{ position:"fixed",bottom:0,left:0,right:0,background:NAVY,display:"flex",flexDirection:"column",zIndex:100,borderTop:"1px solid rgba(255,255,255,.08)",maxWidth:480,margin:"0 auto",paddingBottom:"env(safe-area-inset-bottom, 0px)" }}>
          <div style={{ textAlign:"center",padding:"4px 8px 2px",borderBottom:"1px solid rgba(255,255,255,.06)" }}>
            <span style={{ fontSize:9,color:"rgba(255,255,255,.28)",fontWeight:400,letterSpacing:.3 }}>© 2026 {BRAND_NAME} Services. All rights reserved.</span>
          </div>
          <div style={{ display:"flex",height:56 }}>
            {[
              ["home",    "Home"],
              ["schedule","Book"],
              ["referral","Refer"],
              ["pricing", "Pricing"],
            ].map(([t, label]) => (
              <button key={t} onClick={()=>nav(t)} style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:4,background:"transparent",border:"none",cursor:"pointer",position:"relative",padding:0 }}>
                {tab===t && <div style={{ position:"absolute",top:0,left:"20%",right:"20%",height:2,background:"#42a5f5",borderRadius:"0 0 2px 2px" }}/>}
                <span style={{ fontWeight:700,fontSize:10,letterSpacing:.5,textTransform:"uppercase",color:tab===t?"#42a5f5":"#4a6080" }}>{label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <Toast msg={toast}/>
    </div>
  );
}
